import { cur } from '@/lib/currency';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import api from '@/lib/api';
import { Truck, Download } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { DateQuickFilter } from '@/components/DateQuickFilter';
import { BranchMultiSelect } from '@/components/BranchMultiSelect';
import { FilterEmptyNudge } from '@/components/common/FilterEmptyNudge';
import { localToday } from '@/lib/dateRange';

export default function SupplierSpentPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [dateRange, setDateRange] = useState(null);
  const [branchSel, setBranchSel] = useState([]);
  const [branches, setBranches] = useState([]);
  const start = dateRange?.start || 'all';
  const end = dateRange?.end || 'all';

  const fetchReport = async (range = dateRange, bsel = branchSel) => {
    setLoading(true);
    try {
      const params = {};
      if (range?.start) params.start_date = range.start;
      if (range?.end) params.end_date = range.end;
      if (bsel.length) params.branch_ids = bsel.join(',');
      const res = await api.get('/accounting/supplier-spent-report', { params });
      setData(res.data);
    } catch (err) {
      toast.error('Failed to load report');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReport();
    api.get('/branches').then(r => setBranches(r.data || [])).catch(() => {});
    /* eslint-disable-next-line */
  }, []);

  const chartColors = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#ec4899', '#84cc16', '#f97316', '#14b8a6'];

  const exportCSV = () => {
    if (!data?.suppliers) return;
    const header = ['Supplier', 'Category', 'Purchased (SR)', 'Paid (SR)', 'Returns (SR)', 'Outstanding (SR)', 'Total Spent (SR)', '% Share'];
    const rows = data.suppliers.map(s => [s.supplier_name, s.category || '', s.purchased, s.paid, s.returns || 0, s.outstanding, s.total_spent, `${s.pct_of_total}%`]);
    const csv = [header, ...rows].map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `supplier_spent_${start}_${end}.csv`;
    a.click();
    toast.success('CSV downloaded');
  };

  return (
    <DashboardLayout>
      <div className="space-y-6 p-4 sm:p-6 max-w-7xl mx-auto" data-testid="supplier-spent-page">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold font-outfit flex items-center gap-2">
              <Truck className="text-info" size={26} /> Supplier Spent Report
            </h1>
            <p className="text-sm text-muted-foreground mt-1">Who are you spending the most with, and what % of your total supplier costs does each represent? <span className="text-xs">(Paid = cash/bank paid in range · Outstanding = current balance owed, reconciles with the Supplier Ledger)</span></p>
          </div>
          <div className="flex items-end gap-2 flex-wrap">
            <DateQuickFilter onFilterChange={(range) => { setDateRange(range); fetchReport(range, branchSel); }} />
            <BranchMultiSelect value={branchSel} onChange={(arr) => { setBranchSel(arr); fetchReport(dateRange, arr); }} branches={branches} testid="supplier-spent-branch-multiselect" />
            <Button onClick={() => fetchReport()} disabled={loading} className="h-9" data-testid="refresh-supplier-btn">Refresh</Button>
            <Button variant="outline" className="h-9" onClick={exportCSV} disabled={!data?.suppliers?.length} data-testid="export-supplier-csv-btn"><Download size={14} className="mr-1" />CSV</Button>
          </div>
        </div>

        {/* KPI strip */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          <Card><CardContent className="p-4"><div className="text-xs text-muted-foreground">Total Supplier Spend</div><div className="text-2xl font-bold font-outfit mt-1 text-info">{cur()} {(data?.grand_total || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}</div></CardContent></Card>
          <Card><CardContent className="p-4"><div className="text-xs text-muted-foreground">Active Suppliers</div><div className="text-2xl font-bold font-outfit mt-1">{data?.supplier_count || 0}</div></CardContent></Card>
          <Card><CardContent className="p-4"><div className="text-xs text-muted-foreground">Top Supplier Share</div><div className="text-2xl font-bold font-outfit mt-1 text-warning">{data?.suppliers?.[0]?.pct_of_total || 0}%</div></CardContent></Card>
        </div>

        {/* Top-10 bar chart */}
        {data?.suppliers?.length > 0 && (
          <Card><CardContent className="p-4">
            <div className="text-xs font-semibold text-foreground mb-2">Top 10 Suppliers by Spend</div>
            <ResponsiveContainer width="100%" height={340}>
              <BarChart data={data.suppliers.slice(0, 10)} layout="vertical" margin={{ left: 110 }}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                <XAxis type="number" tick={{ fontSize: 10 }} tickFormatter={(v) => v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v} />
                <YAxis type="category" dataKey="supplier_name" tick={{ fontSize: 10 }} width={120} />
                <Tooltip formatter={(v) => `${cur()} ${Number(v).toLocaleString()}`} />
                <Bar dataKey="total_spent" radius={[0, 4, 4, 0]}>
                  {data.suppliers.slice(0, 10).map((_, i) => <Cell key={i} fill={chartColors[i % chartColors.length]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </CardContent></Card>
        )}

        {/* Supplier table with % share */}
        <Card><CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-xs">
                <tr>
                  <th className="p-3 text-left">#</th>
                  <th className="p-3 text-left">Supplier</th>
                  <th className="p-3 text-left">Category</th>
                  <th className="p-3 text-right" title="Value of goods purchased in the selected date range (all payment modes)">Purchased</th>
                  <th className="p-3 text-right" title="Total cash & bank paid to this supplier in the range: immediately-paid cash/bank purchases + payments settling credit bills (matches the Supplier Ledger)">Paid</th>
                  <th className="p-3 text-right" title="Bill returns + credit supplier returns in the selected date range">Returns</th>
                  <th className="p-3 text-right" title="Current balance still owed to this supplier (all-time) — same as the Supplier Ledger closing balance">Outstanding</th>
                  <th className="p-3 text-right">Total Spent</th>
                  <th className="p-3 text-right">% Share</th>
                </tr>
              </thead>
              <tbody>
                {(data?.suppliers || []).map((s, i) => (
                  <tr key={s.supplier_id} className="border-t hover:bg-muted/50">
                    <td className="p-3 text-muted-foreground text-xs">{i + 1}</td>
                    <td className="p-3 font-medium">{s.supplier_name}</td>
                    <td className="p-3 text-xs text-muted-foreground">{s.category || '—'}</td>
                    <td className="p-3 text-right font-mono text-xs">{s.purchased.toLocaleString(undefined, { maximumFractionDigits: 0 })}</td>
                    <td className="p-3 text-right font-mono text-xs text-success">{s.paid.toLocaleString(undefined, { maximumFractionDigits: 0 })}</td>
                    <td className="p-3 text-right font-mono text-xs text-warning">{(s.returns || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}</td>
                    <td className="p-3 text-right font-mono text-xs text-error">{s.outstanding.toLocaleString(undefined, { maximumFractionDigits: 0 })}</td>
                    <td className="p-3 text-right font-mono font-bold">{s.total_spent.toLocaleString(undefined, { maximumFractionDigits: 0 })}</td>
                    <td className="p-3 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <div className="w-20 bg-muted rounded-full h-1.5 overflow-hidden">
                          <div className="h-full bg-info" style={{ width: `${Math.min(100, s.pct_of_total)}%` }} />
                        </div>
                        <span className="font-mono text-xs min-w-[40px]">{s.pct_of_total}%</span>
                      </div>
                    </td>
                  </tr>
                ))}
                {(!data?.suppliers || data.suppliers.length === 0) && (
                  <tr><td colSpan={9} className="p-0">
                    <FilterEmptyNudge show onReset={() => { const r = { start: localToday(), end: localToday() }; setDateRange(r); setBranchSel([]); fetchReport(r, []); }} label="No supplier spend for the current filter" />
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent></Card>

        {/* Category breakdown */}
        {data?.category_breakdown?.length > 0 && (
          <Card><CardContent className="p-4">
            <div className="text-xs font-semibold text-foreground mb-3">Spend by Category</div>
            <div className="space-y-2">
              {data.category_breakdown.map((c, i) => (
                <div key={c.category} className="flex items-center gap-3">
                  <div className="w-40 text-sm font-medium text-foreground truncate">{c.category}</div>
                  <div className="flex-1 bg-muted rounded-full h-2 overflow-hidden">
                    <div className="h-full rounded-full" style={{ width: `${c.pct}%`, background: chartColors[i % chartColors.length] }} />
                  </div>
                  <div className="font-mono text-xs text-foreground min-w-[100px] text-right">{cur()} {c.total.toLocaleString(undefined, { maximumFractionDigits: 0 })}</div>
                  <div className="font-mono text-xs text-muted-foreground min-w-[50px] text-right">{c.pct}%</div>
                </div>
              ))}
            </div>
          </CardContent></Card>
        )}

        {/* NEW: Per-Branch Cost Ratio — spot branches where cost is eating too much revenue */}
        {data?.branch_comparison?.length > 0 && (
          <Card data-testid="branch-cost-ratio-card"><CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="text-sm font-bold text-foreground">Branch Cost vs Sales Ratio</div>
                <div className="text-xs text-muted-foreground">Higher % = more revenue being consumed by costs (red flag)</div>
              </div>
              <div className="text-[10px] text-muted-foreground uppercase tracking-wider">Sorted by total cost %</div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-[10px] uppercase tracking-wider text-muted-foreground border-b border-border">
                    <th className="text-left p-2">Branch</th>
                    <th className="text-right p-2">Sales</th>
                    <th className="text-right p-2">Supplier Spend</th>
                    <th className="text-right p-2">% of Sales</th>
                    <th className="text-right p-2">Other Expenses</th>
                    <th className="text-right p-2">% of Sales</th>
                    <th className="text-right p-2 bg-warning/10">Total Cost %</th>
                  </tr>
                </thead>
                <tbody>
                  {data.branch_comparison.map((b) => {
                    const tone = b.total_cost_pct_of_sales >= 100 ? 'text-error font-bold bg-error/5' : b.total_cost_pct_of_sales >= 70 ? 'text-warning font-semibold' : 'text-success';
                    return (
                      <tr key={b.branch_id} className="border-b border-border/30 hover:bg-muted/20" data-testid={`branch-ratio-${b.branch_id}`}>
                        <td className="p-2 font-medium text-foreground">
                          {b.branch_id === '__unassigned__' ? (
                            <span className="inline-flex items-center gap-2">
                              <span className="text-warning">{b.branch_name || 'Unassigned'}</span>
                              <button
                                type="button"
                                onClick={() => navigate('/reconcile-unassigned')}
                                className="px-2 py-0.5 rounded bg-warning/15 text-warning hover:bg-warning/25 text-[10px] font-semibold transition-colors"
                                data-testid="supplier-reconcile-unassigned-btn"
                                title="Assign these unassigned sales/expenses to their branch"
                              >
                                Assign to branch →
                              </button>
                            </span>
                          ) : (
                            <button
                              type="button"
                              onClick={() => navigate(`/reconcile-unassigned?branch=${b.branch_id}`)}
                              className="text-left hover:text-primary hover:underline transition-colors"
                              data-testid={`supplier-reconcile-into-${b.branch_id}`}
                              title={`Reconcile unassigned entries into ${b.branch_name}`}
                            >{b.branch_name}</button>
                          )}
                        </td>
                        <td className="p-2 text-right tabular-nums">{cur()} {b.sales.toLocaleString(undefined, {maximumFractionDigits: 0})}</td>
                        <td className="p-2 text-right tabular-nums text-warning">{cur()} {b.supplier_spend.toLocaleString(undefined, {maximumFractionDigits: 0})}</td>
                        <td className="p-2 text-right tabular-nums font-semibold text-warning">{b.supplier_pct_of_sales}%</td>
                        <td className="p-2 text-right tabular-nums text-error">{cur()} {b.other_expenses.toLocaleString(undefined, {maximumFractionDigits: 0})}</td>
                        <td className="p-2 text-right tabular-nums font-semibold text-error">{b.other_exp_pct_of_sales}%</td>
                        <td className={`p-2 text-right tabular-nums ${tone}`}>{b.total_cost_pct_of_sales}%</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="flex items-center gap-3 text-[10px] text-muted-foreground mt-3 pt-3 border-t border-border">
              <div><span className="inline-block w-2 h-2 rounded-full bg-success mr-1"></span>≤70% healthy</div>
              <div><span className="inline-block w-2 h-2 rounded-full bg-warning mr-1"></span>70-99% stretched</div>
              <div><span className="inline-block w-2 h-2 rounded-full bg-error mr-1"></span>≥100% losing money</div>
            </div>
          </CardContent></Card>
        )}
      </div>
    </DashboardLayout>
  );
}
